import type React from 'react'
import { useState } from 'react'
import { Box, Button, Modal, Stack, TextField, Typography } from '@mui/material'

import {
  ChartBlockConfig,
  defaultChartSrc,
  isValidChartId,
  normalizeChartId,
} from './chart-block'

interface Props {
  open: boolean
  onClose: () => void
  onInsert: (config: ChartBlockConfig) => void
}

/** Charts available under public/embeds/. Add a row when adding a page. */
const KNOWN_CHARTS: { id: string; title: string }[] = [
  {
    id: 'aru-basket-vs-gold',
    title: 'Illustrative ARU basket vs Gold and USD',
  },
]

const ChartInsertModal: React.FC<Props> = ({ open, onClose, onInsert }) => {
  const [id, setId] = useState('')
  const [title, setTitle] = useState('')
  const [src, setSrc] = useState('')
  const [srcTouched, setSrcTouched] = useState(false)

  const idValid = isValidChartId(id)
  const effectiveSrc =
    srcTouched && src
      ? src
      : idValid
      ? defaultChartSrc(id, window.location.origin)
      : ''
  const srcValid = /^https:\/\//.test(effectiveSrc)

  const reset = () => {
    setId('')
    setTitle('')
    setSrc('')
    setSrcTouched(false)
  }

  const pickKnown = (chart: { id: string; title: string }) => {
    setId(chart.id)
    setTitle(chart.title)
    setSrc('')
    setSrcTouched(false)
  }

  const handleInsert = () => {
    if (!idValid || !srcValid) return
    onInsert({
      id,
      title: title.trim() || undefined,
      src: effectiveSrc,
    })
    reset()
    onClose()
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 460,
          maxWidth: '90vw',
          bgcolor: 'background.paper',
          borderRadius: 1,
          p: 3,
        }}
      >
        <Typography variant="h6" mb={1}>
          Insert chart
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Embeds an interactive chart page from this CMS into the content.
        </Typography>
        <Stack spacing={2}>
          {KNOWN_CHARTS.length > 0 && (
            <Box display="flex" flexWrap="wrap" gap={1}>
              {KNOWN_CHARTS.map((chart) => (
                <Button
                  key={chart.id}
                  size="small"
                  variant={id === chart.id ? 'contained' : 'outlined'}
                  onClick={() => pickKnown(chart)}
                  data-testid={`chart-pick-${chart.id}`}
                >
                  {chart.title}
                </Button>
              ))}
            </Box>
          )}
          <TextField
            label="Chart ID"
            placeholder="aru-basket-vs-gold"
            value={id}
            onChange={(e) => setId(normalizeChartId(e.target.value))}
            error={id !== '' && !idValid}
            helperText="Lowercase letters, digits and dashes."
            inputProps={{ 'data-testid': 'chart-id-input' }}
            fullWidth
          />
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            inputProps={{ 'data-testid': 'chart-title-input' }}
            fullWidth
          />
          <TextField
            label="Embed URL"
            value={effectiveSrc}
            onChange={(e) => {
              setSrcTouched(true)
              setSrc(e.target.value.trim())
            }}
            error={idValid && !srcValid}
            helperText={
              srcValid || !idValid
                ? 'Defaults to this CMS; change only to embed a chart hosted elsewhere.'
                : 'Must be an https:// URL'
            }
            inputProps={{ 'data-testid': 'chart-src-input' }}
            fullWidth
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleInsert}
              disabled={!idValid || !srcValid}
              data-testid="chart-insert-button"
            >
              Insert
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  )
}

export default ChartInsertModal
